<script lang="ts" setup>
  import DocumentUpload from '@/components/app/cases/documents/DocumentUpload.vue'
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import ChatStreamingLoader from '@/components/app/chat/messages/ChatStreamingLoader.vue'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import OffCanvas from '@/components/ui/OffCanvas.vue'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { ChatPreferencesService } from '@/types/generated/services.gen'
  import type {
    AIChatSession,
    AIChatSessionMessage,
    CustomerCase,
    CustomerOrganisationUser,
    CustomerUser
  } from '@/types/generated'
  import { AIChatSessionTypeEnum, SessionStateEnum } from '@/types/generated'
  import { MetricEventType, useMetrics } from '@/composables/useMetrics'
  import { useIsAiAssistantAppRoute } from '@/composables/useIsAiAssistantAppRoute'
  import { ROUTES } from '@/router/routes'
  import { FileIcon, FilePlus2, MessagesSquare, SquarePen } from 'lucide-vue-next'
  import { computed, inject, nextTick, onMounted, provide, ref, watch, type Ref } from 'vue'
  import { useRoute } from 'vue-router'
  import AiChatMessage from './AiChatMessage.vue'
  import AiChatMessageInput from './AiChatMessageInput.vue'
  import ChatOptionsMenu from './ChatOptionsMenu.vue'
  import FullscreenWelcome from './FullscreenWelcome.vue'
  import SaveChatPopover from './SaveChatPopover.vue'
  import TeamDisclaimer from './TeamDisclaimer.vue'

  // Initialize the case store
  const caseStore = useCaseStore()

  const emit = defineEmits<{
    openPreferencesDialog: []
    'download-pdf': []
    'new-chat': []
    rename: []
    organize: []
    'delete-chat': []
  }>()

  const { t } = useTranslation({
    en: {
      ChatDescription: 'No messages',
      Document: 'Document',
      Documents: 'Documents',
      ChatDocument: 'Chat Document',
      ChatDocuments: 'Chat Documents',
      FolderDocument: 'Folder Document',
      FolderDocuments: 'Folder Documents',
      Participant: 'Party',
      Participants: 'Parties',

      Ready: 'Ready',
      UpdatingContext: 'Updating context',
      SearchingDocuments: 'Searching documents',
      SearchingCourtDecisions: 'Searching court decisions',
      Thinking: 'Thinking',
      Hello: 'Hello',
      HowCanIHelpYou: 'How can I help you?',
      ChooseQuestionTemplate:
        'Choose one of the following question templates or ask your own question.',
      Page: 'Page',
      Pages: 'Pages',
      KIChatHasAccessToAllDocumentsInTheRequest:
        'KI-Chat has access to all documents in the request.',
      DropYourFileHereOr: 'Drop your files here or',
      browse: 'browse',
      Uploading: 'Uploading Documents',
      KIChatHasAccessToAllDataInTheRequest: 'KI-Chat has access to all data in the request.',
      Requester: 'Requester',
      Notary: 'Notary',
      Opponent: 'Opponent',
      Employer: 'Employer',
      UnknownRole: 'Unknown Role',
      context: 'Context',
      GenerateDocument: 'Generate document',

      AiAssistantNoticeTitle: 'Note on AI-generated answers',
      AiAssistantNoticeP1a: 'Answers from JUPUS are generated by ',
      AiAssistantNoticeP1Bold: 'artificial intelligence',
      AiAssistantNoticeP1b:
        ' designed to simplify working with your documents—not to replace core legal work.',
      AiAssistantNoticeP2a: 'The ',
      AiAssistantNoticeP2Bold1: 'processing and generation',
      AiAssistantNoticeP2b: ' of all content happens ',
      AiAssistantNoticeP2Bold2: 'automatically',
      AiAssistantNoticeP2c: '.',
      AiAssistantNoticeP3Bold1: 'Inaccuracies',
      AiAssistantNoticeP3b: ' or ',
      AiAssistantNoticeP3Bold2: 'misinterpretations',
      AiAssistantNoticeP3c: ' may occasionally occur.',
      AiAssistantNoticeP4a: 'For questions, issues, or suggestions, email us at ',
      newChat: 'New Chat'
    },
    de: {
      ChatDescription: 'Ohne Nachrichten',
      Document: 'Dokument',
      Documents: 'Dokumente',
      ChatDocument: 'Chat-Dokument',
      ChatDocuments: 'Chat-Dokumente',
      FolderDocument: 'Ordner-Dokument',
      FolderDocuments: 'Ordner-Dokumente',
      Participant: 'Beteiligter',
      Participants: 'Beteiligte',

      Ready: 'Bereit',
      UpdatingContext: 'Aktualisiere Kontext',
      SearchingDocuments: 'Durchsuche Dokumente',
      SearchingCourtDecisions: 'Durchsuche Gerichtsentscheidungen',
      Thinking: 'Denke nach',
      Hello: 'Hallo',
      HowCanIHelpYou: 'wie kann ich Ihnen helfen?',
      ChooseQuestionTemplate:
        'Wählen Sie eine der folgenden Promptvorlagen oder stellen Sie Ihre eigene Frage.',
      Page: 'Seite',
      Pages: 'Seiten',
      KIChatHasAccessToAllDocumentsInTheRequest:
        'Der KI-Chat hat Zugriff auf alle Dokumente in dieser Anfrage.',
      DropYourFileHereOr: 'Legen Sie Ihre Datei hier ab oder',
      browse: 'durchsuchen',
      Uploading: 'Dokumente werden hochgeladen',
      KIChatHasAccessToAllDataInTheRequest:
        'Der KI-Chat hat Zugriff auf alle Daten in dieser Anfrage.',
      Requester: 'Antragsteller',
      Notary: 'Notar',
      Opponent: 'Gegner',
      Employer: 'Arbeitgeber',
      UnknownRole: 'Unbekannte Rolle',
      context: 'Kontext',
      GenerateDocument: 'Dokument generieren',

      AiAssistantNoticeTitle: 'KI-Hinweis',
      AiAssistantNoticeP1a: 'Die Antworten von JUPUS werden von ',
      AiAssistantNoticeP1Bold: 'künstlicher Intelligenz',
      AiAssistantNoticeP1b:
        ' generiert, die dazu entwickelt wurde, die Arbeit mit Ihren Dokumenten zu vereinfachen und nicht für die Übernahme der kernjuristischen Tätigkeit.',
      AiAssistantNoticeP2a: 'Die ',
      AiAssistantNoticeP2Bold1: 'Verarbeitung und Generierung',
      AiAssistantNoticeP2b: ' aller Inhalte erfolgt ',
      AiAssistantNoticeP2Bold2: 'automatisiert',
      AiAssistantNoticeP2c: '.',
      AiAssistantNoticeP3Bold1: 'Ungenauigkeiten',
      AiAssistantNoticeP3b: ' oder ',
      AiAssistantNoticeP3Bold2: 'Fehlinterpretationen',
      AiAssistantNoticeP3c: ' können gelegentlich auftreten.',
      AiAssistantNoticeP4a:
        'Bei Rückfragen, Problemen oder Verbesserungsvorschlägen melden Sie sich per E-Mail an ',
      newChat: 'Neuer Chat'
    }
  })

  const { translateLawField } = useLawFieldTranslation()

  const isAiAssistantAppRoute = useIsAiAssistantAppRoute()
  const route = useRoute()

  /**
   * Case KI-Chat and global-chat use other entry points for rename/organize/settings;
   * the header overflow menu is hidden on those routes only (still shown e.g. on ai-assistant).
   *
   * :return: Whether the three-dots chat options menu should render in the non–ai-assistant header.
   */
  const showHeaderChatOptionsMenu = computed(() => {
    const path = route.path
    const globalRoot = ROUTES.APP.GLOBAL_CHAT.ROOT
    if (path === globalRoot || path.startsWith(`${globalRoot}/`)) {
      return false
    }
    const casesPrefix = `${ROUTES.APP.CASES.CASE_OVERVIEW}/`
    if (!path.startsWith(casesPrefix)) {
      return true
    }
    const afterCases = path.slice(casesPrefix.length)
    return !/^[^/]+\/chat(?:\/|$)/.test(afterCases)
  })

  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const sessionType = inject<AIChatSessionTypeEnum>('sessionType', AIChatSessionTypeEnum.CASE)

  // Get global chat store to access session groups
  const globalChatStore = useGlobalChatStore()

  // Load session groups when needed for global chat
  watch(
    () => activeChatSession.value?.session_group,
    async (sessionGroupUuid) => {
      if (sessionGroupUuid && sessionType === AIChatSessionTypeEnum.GLOBAL) {
        // Load session groups if not already loaded
        if (globalChatStore.sessionGroups.length === 0) {
          console.log('Loading session groups...')
          await globalChatStore.loadSessionGroups()
        }
      }
    },
    { immediate: true }
  )

  // Set active session group when chat session changes
  watch(
    () => activeChatSession.value,
    async (chatSession) => {
      if (chatSession && sessionType === AIChatSessionTypeEnum.GLOBAL) {
        await globalChatStore.setActiveSessionGroupFromChatSession(chatSession)
        // Processor will be created on-demand when accessed
        globalChatStore.getSessionProcessor(chatSession.uuid)
      }
    },
    { immediate: true }
  )

  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const isActiveSessionGenerating = computed(() => {
    return activeChatSession.value && isSessionGenerating(activeChatSession.value.uuid)
  })
  const currentUser = inject<Ref<CustomerUser | null>>('currentUser', ref(null))

  const activeChatSessionUser = computed<CustomerOrganisationUser | null>(() => {
    if (!activeChatSession.value?.started_by_user) return null

    // For case context, get user from case data
    if (caseData && caseData.value?.org?.users) {
      return (
        caseData.value.org.users.find(
          (user) => user.uuid === activeChatSession.value?.started_by_user
        ) || null
      )
    }

    // For global context, get user from organization users in global store
    if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      return globalChatStore.getOrganisationUserByUuid(activeChatSession.value.started_by_user)
    }

    return null
  })
  const messagesContainer = ref<HTMLElement | null>(null)
  const messages = computed(() => activeChatSession.value?.messages)
  const hasStreamingMessage = computed(
    () => messages.value?.some((m: any) => m._isStreaming === true) ?? false
  )
  const hasMessages = computed(() => (messages.value?.length ?? 0) > 0)

  /** After the first send from the welcome screen, keep layout top-aligned while the session is created. */
  const aiAssistantWelcomeSendPending = ref(false)

  /** Figma: composer + welcome stack vertically centered until the first message exists. */
  const centerAiAssistantComposer = computed(
    () => isAiAssistantAppRoute.value && !hasMessages.value && !aiAssistantWelcomeSendPending.value
  )

  // Show the welcome whenever the AI Assistant fullscreen has no chat content yet.
  // Gating on `!hasMessages` (rather than `!activeChatSession`) keeps the welcome
  // visible when "New Chat" creates an empty session but no messages exist yet.
  const showFullscreenWelcome = computed(
    () =>
      sessionType === AIChatSessionTypeEnum.GLOBAL &&
      isAiAssistantAppRoute.value &&
      !hasMessages.value
  )

  const fullscreenTopTemplates = computed(() => {
    const templates = globalChatStore.promptTemplates || []
    const favorites = templates.filter((tmpl) => tmpl.is_favorite === 'true')
    if (favorites.length >= 4) {
      const jupusFavs = favorites.filter((tmpl) => tmpl.is_jupus_template)
      const otherFavs = favorites.filter((tmpl) => !tmpl.is_jupus_template)
      return [...jupusFavs, ...otherFavs].slice(0, 3)
    }
    const jupus = templates.filter((tmpl) => tmpl.is_jupus_template)
    const rest = templates.filter((tmpl) => !tmpl.is_jupus_template)
    return [...jupus, ...rest].slice(0, 3)
  })

  const sendChatMessage = inject(
    'sendChatMessage',
    async (_session: AIChatSession | null, _message: string) => {}
  )
  // Use document panel state from the injected value
  const isDocumentPanelOpen = inject<Ref<boolean>>('isDocumentPanelOpen', ref(false))
  const showSessionStateProgress = computed(
    () => activeChatSession.value?.session_state !== SessionStateEnum.READY
  )
  const stateMessage = computed(() => {
    const stateMessages: Record<SessionStateEnum, string> = {
      [SessionStateEnum.READY]: t('Ready'),
      [SessionStateEnum.UPDATING_CONTEXT]: t('UpdatingContext'),
      [SessionStateEnum.SEARCHING_DOCUMENTS]: t('SearchingDocuments'),
      [SessionStateEnum.SEARCHING_COURT_DECISIONS]: t('SearchingCourtDecisions'),
      [SessionStateEnum.REASONING]: t('Thinking')
    }
    const currentState = activeChatSession.value?.session_state as SessionStateEnum
    return currentState ? stateMessages[currentState] : ''
  })

  const lastUserMessage = computed(() => {
    if (!activeChatSession.value?.messages) return ''
    const userMessages = activeChatSession.value.messages
      .filter((msg) => msg.role === 'user')
      .sort((a, b) => new Date(b.created_at || '') - new Date(a.created_at || ''))
    return userMessages[0]?.content || ''
  })

  const truncatedLastMessage = computed(() => {
    const msg = lastUserMessage.value
    return msg.length > 50 ? msg.slice(0, 50) + '...' : msg
  })

  const scrollToBottom = async () => {
    await nextTick()
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  }

  const updateMessage = (updatedMessage: AIChatSessionMessage) => {
    if (activeChatSession.value?.messages) {
      const messageIndex = activeChatSession.value.messages.findIndex(
        (msg) => msg.uuid === updatedMessage.uuid
      )
      if (messageIndex !== -1) {
        activeChatSession.value.messages[messageIndex] = updatedMessage
      }
    }
  }

  watch(
    () => messages.value,
    () => {
      scrollToBottom()
    },
    { deep: true }
  )

  const settingsCount = ref(0)

  onMounted(async () => {
    scrollToBottom()
    try {
      const res = await ChatPreferencesService.chatPreferencesList({ limit: 1 })
      settingsCount.value = res.count ?? 0
    } catch {
      // silently fail
    }
  })

  // Show/hide success message when documents start/complete processing
  watch(
    () => {
      // For global chat, watch both processors
      if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
        const chatProcessor = globalChatStore.chatSessionProcessor
        const folderProcessor = globalChatStore.folderProcessor
        return (
          (chatProcessor?.processingDocuments?.length || 0) +
          (folderProcessor?.processingDocuments?.length || 0)
        )
      }
      // For case chat, watch case processor
      return caseStore.documentProcessor.processingDocuments.length
    },
    (newCount, oldCount) => {
      // If processing count decreased to zero, show success message
      if (oldCount > 0 && newCount === 0) {
        showSuccessMessage.value = true

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          showSuccessMessage.value = false
        }, 5000)
      }
      // If processing count increased from zero, hide success message
      else if (oldCount === 0 && newCount > 0) {
        showSuccessMessage.value = false
      }
    }
  )

  const getThumbnailUrl = (doc: any) => {
    if (!doc.images?.length) return null
    const thumbnail = doc.images.find((img: any) => img.image_preset === 'thumbnail')
    return thumbnail?.url || null
  }

  const { trackEvent } = useMetrics()

  // Number of templates to show initially
  const initialTemplateCount = 6

  const isPromptPopoverOpen = ref(false)
  provide('isPromptPopoverOpen', isPromptPopoverOpen)
  provide('sessionType', sessionType)
  provide('isAiAssistantAppRoute', isAiAssistantAppRoute)
  provide('centerAiAssistantComposer', centerAiAssistantComposer)

  // Function to open the prompt popover
  const openPromptPopover = () => {
    isPromptPopoverOpen.value = true
  }

  const onFullscreenWelcomeSend = async (prompt: string) => {
    aiAssistantWelcomeSendPending.value = true
    try {
      await sendChatMessage(null, prompt)
    } finally {
      aiAssistantWelcomeSendPending.value = false
    }
  }

  const onFullscreenWelcomeShowAll = () => {
    openPromptPopover()
  }

  // Compute the templates to display
  const visibleTemplates = computed(() => {
    // For global context, use global prompt templates
    if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      const allTemplates = globalChatStore.promptTemplates || []

      if (allTemplates.length <= initialTemplateCount) {
        return allTemplates
      }

      // Prioritize favorited templates
      const favoritedTemplates = allTemplates.filter((template) => template.is_favorite)
      const nonFavoritedTemplates = allTemplates.filter((template) => !template.is_favorite)

      // If we have enough favorited templates to fill the initial count, use only those
      if (favoritedTemplates.length >= initialTemplateCount) {
        return favoritedTemplates.slice(0, initialTemplateCount)
      }

      // Otherwise, show all favorited templates and fill the rest with non-favorited ones
      return [
        ...favoritedTemplates,
        ...nonFavoritedTemplates.slice(0, initialTemplateCount - favoritedTemplates.length)
      ]
    }

    // For case context, use case templates
    if (!caseData || !caseData.value?.org?.ai_chat_question_templates) {
      return []
    }

    const allTemplates = caseData.value.org.ai_chat_question_templates

    if (allTemplates.length <= initialTemplateCount) {
      return allTemplates
    }

    // Prioritize favorited templates
    const favoritedTemplates = allTemplates.filter((template) => template.is_favorite)
    const nonFavoritedTemplates = allTemplates.filter((template) => !template.is_favorite)

    // If we have enough favorited templates to fill the initial count, use only those
    if (favoritedTemplates.length >= initialTemplateCount) {
      return favoritedTemplates.slice(0, initialTemplateCount)
    }

    // Otherwise, show all favorited templates and fill the rest with non-favorited ones
    return [
      ...favoritedTemplates,
      ...nonFavoritedTemplates.slice(0, initialTemplateCount - favoritedTemplates.length)
    ]
  })

  const hasMoreTemplates = computed(() => {
    if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      const allTemplates = globalChatStore.promptTemplates || []
      return allTemplates.length > visibleTemplates.value.length
    }
    if (!caseData || !caseData.value?.org?.ai_chat_question_templates) return false
    return caseData.value.org.ai_chat_question_templates.length > visibleTemplates.value.length
  })

  const remainingTemplatesCount = computed(() => {
    if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      const allTemplates = globalChatStore.promptTemplates || []
      return Math.max(0, allTemplates.length - visibleTemplates.value.length)
    }
    if (!caseData || !caseData.value?.org?.ai_chat_question_templates) return 0
    return Math.max(
      0,
      caseData.value.org.ai_chat_question_templates.length - visibleTemplates.value.length
    )
  })

  // Simplified state tracking - we'll rely on the document module for processing state
  const showSuccessMessage = ref(false)
</script>

<template>
  <div
    class="flex h-full flex-1 flex-col"
    data-cy="chat-main-container"
  >
    <div class="flex h-full flex-col">
      <div class="relative flex h-full flex-col overflow-hidden">
        <div class="flex h-full flex-col overflow-hidden">
          <!-- Fullscreen KI-Assistent: minimal top bar (Figma), no in-panel user profile -->
          <header
            v-if="isAiAssistantAppRoute"
            class="sticky top-0 z-[9] flex h-12 shrink-0 items-center justify-between border-b border-gray-100 bg-white
              px-4 dark:border-neutral-800 dark:bg-neutral-900 sm:h-14 sm:px-6"
            data-cy="chat-actions-minimal"
          >
            <span
              class="flex size-9 items-center justify-center text-gray-500 dark:text-neutral-400"
              aria-hidden="true"
            >
              <MessagesSquare
                class="size-6 shrink-0"
                :stroke-width="1.65"
              />
            </span>
            <div class="flex items-center gap-2 text-gray-500 dark:text-neutral-400">
              <button
                v-if="activeChatSession"
                type="button"
                class="flex size-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100
                  dark:text-neutral-400 dark:hover:bg-neutral-800"
                :aria-label="t('newChat')"
                data-cy="new-chat-button"
                @click="emit('new-chat')"
              >
                <SquarePen
                  class="size-5"
                  :stroke-width="1.65"
                />
              </button>
              <SaveChatPopover
                v-if="activeChatSession"
                :case-data="caseData?.uuid ? caseData : null"
              />
              <ChatOptionsMenu
                :has-messages="hasMessages"
                :is-streaming="isActiveSessionGenerating"
                :settings-count="settingsCount || undefined"
                @download-pdf="emit('download-pdf')"
                @rename="emit('rename')"
                @organize="emit('organize')"
                @delete="emit('delete-chat')"
                @settings="emit('openPreferencesDialog')"
              />
            </div>
          </header>
          <header
            v-else
            class="sticky top-0 z-[9] flex items-center justify-between gap-x-2 rounded-b-none rounded-r-lg border-b
              border-gray-200 bg-white p-2 dark:border-neutral-700 dark:bg-neutral-800 sm:px-5"
            data-cy="chat-actions"
          >
            <!-- User Info -->
            <div>
              <button
                type="button"
                class="flex items-center gap-x-3.5 truncate focus:outline-none"
              >
                <UserAvatar
                  v-if="activeChatSessionUser || currentUser"
                  :user="activeChatSessionUser || currentUser"
                  size="32px"
                />
                <span class="grow truncate text-center lg:text-start">
                  <span
                    class="block truncate text-sm font-semibold leading-4 text-gray-800 dark:text-neutral-200"
                  >
                    {{ (activeChatSessionUser || currentUser)?.first_name }}
                    {{ (activeChatSessionUser || currentUser)?.last_name }}
                  </span>
                  <span
                    class="block truncate text-xs leading-4 text-gray-500 dark:text-neutral-500"
                  >
                    {{ truncatedLastMessage || t('ChatDescription') }}
                  </span>
                </span>
              </button>
            </div>

            <div class="flex items-center gap-3 text-gray-500 dark:text-neutral-400">
              <!-- Save chat as PDF (only when there's an active session) -->
              <SaveChatPopover
                v-if="activeChatSession"
                :case-data="caseData?.uuid ? caseData : null"
              />
              <!-- Generate document button (case only) -->
              <a
                v-if="caseData?.uuid"
                :href="`${ROUTES.APP.CASES.CASE_DETAIL.DOCUMENT_GENERATION.replace(':uuid', caseData.uuid)}?source=ai_chat`"
                class="flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 text-sm font-medium
                  text-gray-800 shadow-sm transition-colors hover:bg-gray-50 dark:border-neutral-700
                  dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
                @click="
                  trackEvent(MetricEventType.AI_FUNCTION_DOCUMENT_GENERATION_USED, {
                    source: 'ai_chat'
                  })
                "
              >
                <FilePlus2 class="size-4 shrink-0" />
                {{ t('GenerateDocument') }}
              </a>
              <!-- Three-dots contextual menu: hidden on /app/cases/:uuid/chat and /app/global-chat (see showHeaderChatOptionsMenu). -->
              <ChatOptionsMenu
                v-if="showHeaderChatOptionsMenu"
                :has-messages="hasMessages"
                :is-streaming="isActiveSessionGenerating"
                :settings-count="settingsCount || undefined"
                @download-pdf="emit('download-pdf')"
                @rename="emit('rename')"
                @organize="emit('organize')"
                @delete="emit('delete-chat')"
                @settings="emit('openPreferencesDialog')"
              />
            </div>
          </header>

          <!-- /app/ai-assistant only: Figma layout (centered composer, fullscreen welcome) -->
          <div
            v-if="isAiAssistantAppRoute"
            class="flex min-h-0 flex-1 overflow-hidden"
          >
            <div
              class="flex min-h-0 flex-1 flex-col overflow-hidden transition-all duration-300"
              data-cy="chat-main-content"
            >
              <div
                v-show="!centerAiAssistantComposer"
                ref="messagesContainer"
                class="min-h-0 flex-1 overflow-y-auto bg-white"
                data-cy="chat-messages-container"
              >
                <div class="mx-auto flex max-w-4xl flex-col gap-6 px-4 py-6 sm:px-8">
                  <div
                    v-if="messages?.length > 0"
                    class="mt-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-gray-700
                      dark:border-yellow-900/30 dark:bg-yellow-900/20 dark:text-gray-200"
                  >
                    <h4 class="mb-2 font-medium">{{ t('AiAssistantNoticeTitle') }}</h4>
                    <p class="mb-1">
                      {{ t('AiAssistantNoticeP1a') }}
                      <span class="font-bold">{{ t('AiAssistantNoticeP1Bold') }}</span>
                      {{ t('AiAssistantNoticeP1b') }}
                    </p>
                    <p class="mb-1">
                      {{ t('AiAssistantNoticeP2a') }}
                      <span class="font-bold">{{ t('AiAssistantNoticeP2Bold1') }}</span>
                      {{ t('AiAssistantNoticeP2b') }}
                      <span class="font-bold">{{ t('AiAssistantNoticeP2Bold2') }}</span>
                      {{ t('AiAssistantNoticeP2c') }}
                    </p>
                    <p class="mb-1">
                      <span class="font-bold">{{ t('AiAssistantNoticeP3Bold1') }}</span>
                      {{ t('AiAssistantNoticeP3b') }}
                      <span class="font-bold">{{ t('AiAssistantNoticeP3Bold2') }}</span>
                      {{ t('AiAssistantNoticeP3c') }}
                    </p>
                    <p>
                      {{ t('AiAssistantNoticeP4a') }}
                      <a
                        href="mailto:support@jupus.de"
                        class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        support@jupus.de
                      </a>
                    </p>
                  </div>
                  <AiChatMessage
                    v-for="(message, idx) in messages"
                    :key="message.uuid"
                    :message="message"
                    :is-latest-message="idx === messages.length - 1"
                    @open-preferences-dialog="emit('openPreferencesDialog')"
                    @update-message="updateMessage"
                  />
                  <div
                    v-if="isActiveSessionGenerating && !hasStreamingMessage"
                    class="flex flex-col items-start gap-2"
                    data-cy="ai-typing-indicator"
                  >
                    <div
                      v-if="showSessionStateProgress"
                      class="flex items-center gap-2 text-xs text-gray-500 dark:text-neutral-400"
                    >
                      <div class="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500"></div>
                      {{ stateMessage }}
                    </div>
                    <ChatStreamingLoader />
                  </div>
                </div>
              </div>

              <div
                class="flex min-w-0 shrink-0 flex-col bg-white dark:bg-neutral-900"
                :class="
                  centerAiAssistantComposer && 'min-h-0 flex-1 justify-center overflow-y-auto'
                "
              >
                <div
                  class="flex w-full min-w-0 max-w-full flex-col"
                  :class="
                    centerAiAssistantComposer
                      ? 'mx-auto max-w-5xl items-center gap-10 px-4 py-8 sm:gap-12 sm:px-6 sm:py-10'
                      : ''
                  "
                >
                  <FullscreenWelcome
                    v-if="showFullscreenWelcome && centerAiAssistantComposer"
                    :first-name="currentUser?.first_name"
                    :last-name="currentUser?.last_name"
                    :show-create-document-quick-action="fullscreenTopTemplates.length >= 1"
                    :show-todo-from-case-quick-action="fullscreenTopTemplates.length >= 2"
                    :show-show-all-quick-action="(globalChatStore.promptTemplates?.length ?? 0) > 0"
                    :create-document-quick-action-prompt="fullscreenTopTemplates[0]?.prompt"
                    :todo-from-case-quick-action-prompt="fullscreenTopTemplates[1]?.prompt"
                    @send-suggestion="onFullscreenWelcomeSend"
                    @show-all="onFullscreenWelcomeShowAll"
                  />
                  <div
                    class="flex w-full min-w-0 max-w-full flex-col gap-1"
                    :class="centerAiAssistantComposer && 'items-stretch'"
                  >
                    <AiChatMessageInput />
                    <TeamDisclaimer />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Global chat + case side panel: unchanged vs origin/master -->
          <div
            v-else
            class="flex flex-1 overflow-hidden"
          >
            <div
              class="flex flex-1 flex-col overflow-hidden transition-all duration-300"
              data-cy="chat-main-content"
            >
              <div
                ref="messagesContainer"
                class="flex-1 overflow-y-auto bg-gray-50"
                data-cy="chat-messages-container"
              >
                <div class="mx-auto max-w-4xl space-y-5 p-6">
                  <div
                    v-if="!messages?.length"
                    class="flex flex-col items-center justify-center px-4"
                  >
                    <h2 class="pt-6 text-center text-3xl font-semibold tracking-tight sm:text-4xl">
                      👋
                      <div class="mt-4">
                        {{ t('Hello') }} {{ currentUser?.first_name }} {{ currentUser?.last_name }},
                        <br />
                        <span
                          class="bg-gradient-to-r from-pink-600 via-red-500 to-purple-600 bg-clip-text text-transparent"
                        >
                          {{ t('HowCanIHelpYou') }}
                        </span>
                      </div>
                    </h2>
                    <p class="mt-4 text-gray-600 dark:text-gray-400">
                      {{ t('ChooseQuestionTemplate') }}
                    </p>
                  </div>
                  <div
                    v-if="!messages?.length"
                    class="mt-8"
                    data-cy="chat-templates-section"
                  >
                    <!-- Templates Grid -->
                    <div class="flex flex-col items-center gap-3 px-4 md:px-8">
                      <div class="grid max-w-md grid-cols-2 gap-3">
                        <button
                          v-for="template in visibleTemplates"
                          :key="template.uuid"
                          class="inline-flex items-center justify-center gap-x-1.5 truncate rounded-full border border-purple-100
                            bg-purple-50/50 px-3 py-1.5 text-sm transition-colors hover:border-purple-200 hover:bg-purple-50
                            dark:border-purple-900/30 dark:bg-purple-900/10 dark:hover:border-purple-900/50
                            dark:hover:bg-purple-900/20"
                          data-cy="template-button"
                          @click="sendChatMessage(activeChatSession, template.prompt)"
                        >
                          <span
                            v-if="template.law_field"
                            class="shrink-0 font-medium text-purple-900 dark:text-purple-100"
                          >
                            {{ translateLawField(template.law_field) }}:
                          </span>
                          <span
                            :class="[
                              template.law_field
                                ? 'text-purple-700'
                                : 'font-medium text-purple-900',
                              'truncate dark:text-purple-300'
                            ]"
                          >
                            {{ template.title }}
                          </span>
                        </button>
                        <!-- Show More Link -->
                        <button
                          v-if="hasMoreTemplates"
                          class="col-span-2 inline-flex items-center justify-center rounded-full px-3 py-1.5 text-sm font-medium
                            text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
                          @click="openPromptPopover"
                        >
                          + {{ remainingTemplatesCount }}
                          {{ remainingTemplatesCount === 1 ? 'template' : 'templates' }} more
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="messages?.length > 0"
                    class="mt-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-gray-700
                      dark:border-yellow-900/30 dark:bg-yellow-900/20 dark:text-gray-200"
                  >
                    <h4 class="mb-2 font-medium">{{ t('AiAssistantNoticeTitle') }}</h4>
                    <p class="mb-1">
                      {{ t('AiAssistantNoticeP1a') }}
                      <span class="font-bold">{{ t('AiAssistantNoticeP1Bold') }}</span>
                      {{ t('AiAssistantNoticeP1b') }}
                    </p>
                    <p class="mb-1">
                      {{ t('AiAssistantNoticeP2a') }}
                      <span class="font-bold">{{ t('AiAssistantNoticeP2Bold1') }}</span>
                      {{ t('AiAssistantNoticeP2b') }}
                      <span class="font-bold">{{ t('AiAssistantNoticeP2Bold2') }}</span>
                      {{ t('AiAssistantNoticeP2c') }}
                    </p>
                    <p class="mb-1">
                      <span class="font-bold">{{ t('AiAssistantNoticeP3Bold1') }}</span>
                      {{ t('AiAssistantNoticeP3b') }}
                      <span class="font-bold">{{ t('AiAssistantNoticeP3Bold2') }}</span>
                      {{ t('AiAssistantNoticeP3c') }}
                    </p>
                    <p>
                      {{ t('AiAssistantNoticeP4a') }}
                      <a
                        href="mailto:support@jupus.de"
                        class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        support@jupus.de
                      </a>
                    </p>
                  </div>
                  <AiChatMessage
                    v-for="(message, idx) in messages"
                    :key="message.uuid"
                    :message="message"
                    :is-latest-message="idx === messages.length - 1"
                    @open-preferences-dialog="emit('openPreferencesDialog')"
                    @update-message="updateMessage"
                  />
                  <div
                    v-if="isActiveSessionGenerating && !hasStreamingMessage"
                    class="flex gap-x-2 sm:gap-x-4"
                    data-cy="ai-typing-indicator"
                  >
                    <!-- AI Avatar -->
                    <div
                      class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100"
                    >
                      <span class="text-xs font-medium text-purple-600">AI</span>
                    </div>
                    <div class="w-full max-w-[90%] grow space-y-3 md:max-w-2xl">
                      <div
                        class="space-y-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900"
                      >
                        <div class="text-sm text-gray-800 dark:text-white">
                          <div class="space-y-4">
                            <div
                              v-if="showSessionStateProgress"
                              class="flex items-center gap-2 text-xs text-gray-500"
                            >
                              <div class="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500"></div>
                              {{ stateMessage }}
                            </div>
                            <div class="space-y-2">
                              <SkeletonLoader class="h-4 w-3/4" />
                              <SkeletonLoader class="h-4 w-1/2" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <AiChatMessageInput />
            </div>
          </div>
        </div>
      </div>
    </div>
    <OffCanvas
      v-model="isDocumentPanelOpen"
      position="right"
      width="w-80"
    >
      <template #title>
        {{ caseStore.documentProcessor.documents.length === 1 ? t('Document') : t('Documents') }}
      </template>

      <div
        v-for="item in caseStore.documentProcessor.documents"
        :key="item.uuid"
        class="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2 dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div
          class="relative h-10 w-10 shrink-0 overflow-hidden rounded-md border border-gray-200
            dark:border-neutral-700"
        >
          <img
            v-if="item.document?.images?.length"
            :src="getThumbnailUrl(item.document)"
            :alt="item.document?.title"
            class="h-full w-full object-cover"
          />
          <div
            v-else
            class="flex h-full w-full items-center justify-center bg-gray-100 text-gray-400 dark:bg-neutral-700
              dark:text-neutral-500"
          >
            <FileIcon class="h-5 w-5" />
          </div>
        </div>

        <div class="min-w-0 flex-1">
          <h4 class="truncate text-sm font-medium text-gray-900 dark:text-white">
            {{ item.document?.title }}
          </h4>
          <div class="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-neutral-400">
            <span>
              {{
                item.document?.created_at &&
                new Date(item.document.created_at).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })
              }}
            </span>
            <span>•</span>
            <span>
              {{
                item.document?.page_count === 1
                  ? `1 ${t('Page')}`
                  : `${item.document?.page_count} ${t('Pages')}`
              }}
            </span>
          </div>
        </div>
      </div>
      <div class="upload-section">
        <DocumentUpload
          v-if="caseData"
          :case-data="caseData"
          :auto-remove-after-ai-ready="true"
          :show-processing-documents="true"
        />
      </div>
    </OffCanvas>
  </div>
</template>

<style>
  .slide-enter-active,
  .slide-leave-active {
    transition: transform 0.2s ease-out;
  }

  .slide-enter-from,
  .slide-leave-to {
    transform: translateX(100%);
  }

  .slide-enter-to,
  .slide-leave-from {
    transform: translateX(0);
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
  }

  .upload-section {
    max-height: 120px;
    overflow-y: auto;
    background-color: var(--background);
    border-radius: 4px;
  }

  .upload-section:empty {
    display: none;
  }

  /* Custom scrollbar styling for messages container */
  .overflow-y-auto::-webkit-scrollbar {
    width: 8px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 0.5rem;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 0.5rem;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
</style>
